import { setUploadProgress } from "../slices/uploadSlice";
import { baseApi } from "./baseApi";

export const adminApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Dashboard and Analytics
    getDashboardStats: builder.query({
      query: () => "/admin/dashboard",
      providesTags: ["Dashboard"],
    }),

    getAnalytics: builder.query({
      query: () => "/admin/analytics",
      providesTags: ["Analytics"],
      transformResponse: (response) => {
        console.log("Analytics response:", response);
        return response?.analytics;
      },
    }),

    // User Management
    getUsers: builder.query({
      query: (params = {}) => ({
        url: "/admin/users",
        params,
      }),
      providesTags: ["User"],
    }),

    createUser: builder.mutation({
      query: (user) => ({
        url: "/admin/users",
        method: "POST",
        body: user,
      }),
      invalidatesTags: ["User", "Classes", "ClassExams"],
    }),

    addUserArray: builder.mutation({
      query: (users) => ({
        url: "/admin/users/students/bulk-create",
        method: "POST",
        body: users,
      }),
      invalidatesTags: ["User", "Classes", "ClassExams"],
    }),

    updateUserRole: builder.mutation({
      query: ({ userId, role }) => ({
        url: `/admin/users/${userId}/role`,
        method: "PATCH",
        body: { role },
      }),
      invalidatesTags: ["User", "Analytics"],
    }),

    updateUser: builder.mutation({
      query: ({ userId, userData }) => ({
        url: `/admin/users/${userId}`,
        method: "PUT",
        body: userData,
      }),
      invalidatesTags: ["User", "Analytics"],
    }),

    deleteUser: builder.mutation({
      query: (userId) => ({
        url: `/admin/users/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["User", "Analytics"],
    }),

    // Assignment Management
    getAssignments: builder.query({
      query: (params = {}) => ({
        url: "/admin/assignments",
        params,
      }),
      providesTags: ["Assignment"],
    }),

    getAssignmentSubmissions: builder.query({
      query: (assignmentId) => `/admin/assignments/${assignmentId}/submissions`,
      providesTags: (result, error, assignmentId) => [
        { type: "AssignmentSubmission", id: assignmentId },
      ],
    }),

    deleteAssignment: builder.mutation({
      query: (assignmentId) => ({
        url: `/admin/assignments/${assignmentId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Assignment", "Analytics"],
    }),

    // Exam Management
    getAdminExams: builder.query({
      query: (params = {}) => ({
        url: "/admin/exams",
        params,
      }),
      providesTags: ["Exam"],
    }),

    getExamById: builder.query({
      query: (examId) => ({
        url: `/admin/exams/${examId}`,
      }),
      providesTags: (result, error, examId) => [{ type: "Exam", id: examId }],
    }),

    createExam: builder.mutation({
      query: (examData) => ({
        url: "/admin/exams",
        method: "POST",
        body: examData,
      }),
      invalidatesTags: [
        "Exam",
        "Analytics",
        "PublicMockTest",
        "PublicMockTestStats",
      ],
    }),

    updateAdminExam: builder.mutation({
      query: ({ examId, examData }) => ({
        url: `/admin/exams/${examId}`,
        method: "PUT",
        body: examData, // Can be FormData or JSON - browser handles Content-Type
      }),
      // Note: DO NOT manually set Content-Type for FormData
      // The browser automatically sets it with the correct boundary
      invalidatesTags: [
        "Exam",
        "Analytics",
        "PublicMockTest",
        "PublicMockTestStats",
      ],
    }),

    getExamAttempts: builder.query({
      query: (examId) => `/admin/exams/${examId}/attempts`,
      providesTags: (result, error, examId) => [
        { type: "ExamAttempt", id: examId },
      ],
    }),

    getAllExamAttempts: builder.query({
      query: (params = {}) => ({
        url: "/admin/exam-attempts",
        params,
      }),
      providesTags: ["ExamAttempt"],
    }),

    deleteExamAttempt: builder.mutation({
      query: (attemptId) => ({
        url: `/admin/exam-attempts/${attemptId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ExamAttempt", "Analytics"],
    }),

    resetExamAttempt: builder.mutation({
      query: (attemptId) => ({
        url: `/admin/exam-attempts/${attemptId}/reset`,
        method: "POST",
      }),
      invalidatesTags: ["ExamAttempt"],
    }),

    deleteAdminExam: builder.mutation({
      query: (examId) => ({
        url: `/admin/exams/${examId}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "Exam",
        "Analytics",
        "PublicMockTest",
        "PublicMockTestStats",
      ],
    }),

    // Parse a PDF (no DB writes). Returns parsed questions + passages.
    parsePdf: builder.mutation({
      query: (formData) => ({
        url: "/admin/pdf/parse",
        method: "POST",
        body: formData,
      }),
    }),

    // Bulk-add curated questions to an existing exam.
    bulkAddExamQuestions: builder.mutation({
      query: ({ examId, section, questions }) => ({
        url: `/admin/exams/${examId}/questions/bulk`,
        method: "POST",
        body: { section, questions },
      }),
      invalidatesTags: (result, error, { examId }) => [
        { type: "Exam", id: examId },
        "Exam",
        "ExamQuestion",
        "PartQuestion",
        "Analytics",
      ],
    }),

    // Parse a PDF (no DB writes). Returns parsed questions + passages.
    parsePdf: builder.mutation({
      query: (formData) => ({
        url: "/admin/pdf/parse",
        method: "POST",
        body: formData,
      }),
    }),

    // Bulk-add curated questions to an existing exam.
    bulkAddExamQuestions: builder.mutation({
      query: ({ examId, section, questions }) => ({
        url: `/admin/exams/${examId}/questions/bulk`,
        method: "POST",
        body: { section, questions },
      }),
      invalidatesTags: (result, error, { examId }) => [
        { type: "Exam", id: examId },
        "Exam",
        "ExamQuestion",
        "PartQuestion",
        "Analytics",
      ],
    }),

    getAdminExamQuestions: builder.query({
      query: (examId) => `/admin/exams/${examId}/questions`,
      providesTags: (result, error, examId) => [
        { type: "ExamQuestion", id: examId },
      ],
    }),

    getQuestionsByPartId: builder.query({
      query: ({examId, partId}) => `/admin/exams/${examId}/part/${partId}/questions`,
      providesTags: (result, error, {partId, examId}) => [
        { type: "PartQuestion", id: partId },
        // { type: "ExamQuestion", id: examId },
      ],
    }),

    addQuestionToExam: builder.mutation({
      query: ({ examId, questionData, partId }) => ({
        url: `/admin/exams/${examId}/part/${partId}/questions`,
        method: "POST",
        body: questionData,
      }),
      // Instead of `query`, we use `queryFn` for full control.
//       queryFn: async ({ examId, questionData, partId }, { dispatch }, api, meta) => {
//         // `examData` is the FormData object from your component
//         return new Promise((resolve) => {
//           const xhr = new XMLHttpRequest();

//           // Listen for upload progress
//           xhr.upload.onprogress = (event) => {
//             if (event.lengthComputable) {
//               const percentComplete = Math.round(
//                 (event.loaded / event.total) * 100
//               );
//               // Dispatch the progress to the Redux store
//               dispatch(setUploadProgress(percentComplete));
//             }
//           };

//           // Handle completion
//           xhr.onload = () => {
//             if (xhr.status >= 200 && xhr.status < 300) {
//               // On success, resolve with the data
//               resolve({ data: JSON.parse(xhr.responseText) });
//             } else {
//               // On failure, resolve with an error object
//               resolve({
//                 error: {
//                   status: xhr.status,
//                   data: JSON.parse(xhr.responseText),
//                 },
//               });
//             }
//           };

//           // Handle network errors
//           xhr.onerror = () => {
//             resolve({
//               error: {
//                 status: "NETWORK_ERROR",
//                 error: "A network error occurred.",
//               },
//             });
//           };
// console.log('process.env.REACT_APP_API_URL', process.env.REACT_APP_API_URL);
//           // Configure and send the request
//           xhr.open(
//             "POST",
//             process.env.REACT_APP_API_URL + `/admin/exams/${examId}/part/${partId}/questions`,
//             true
//           ); // Full URL for the exam endpoint

//           const token = localStorage.getItem("token");
//           if (token) {
//             xhr.setRequestHeader("Authorization", `Bearer ${token}`);
//           }

//           xhr.send(questionData); // Send the FormData
//         });
//       },
      invalidatesTags: (result, error, { examId, partId }) => [
        { type: "Exam", id: examId },
        "Exam",
        // { type: "ExamQuestion", id: examId },
        { type: "PartQuestion", id: partId },
      ],
    }),

    updateQuestionInExam: builder.mutation({
      query: ({ examId, partId, questionId, questionData }) => ({
        url: `/admin/exams/${examId}/part/${partId}/questions/${questionId}`,
        method: "PATCH",
        body: questionData,
      }),
      invalidatesTags: (result, error, { examId, partId }) => [
        { type: "Exam", id: examId },
        "Exam",
        // { type: "ExamQuestion", id: examId },
        { type: "PartQuestion", id: partId },
      ],
    }),

    deleteQuestionFromExam: builder.mutation({
      query: ({ examId, partId, questionId }) => ({
        url: `/admin/exams/${examId}/part/${partId}/questions/${questionId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { examId, partId }) => [
        { type: "Exam", id: examId },
        "Exam",
        // { type: "ExamQuestion", id: examId },
        { type: "PartQuestion", id: partId },
      ],
    }),

    updateExamPart: builder.mutation({
      query: ({ examId, partData }) => ({
        url: `/admin/exams/${examId}`,
        method: "PATCH",
        body: partData, // Expects FormData
      }),
      invalidatesTags: (result, error, { examId, partData }) => [
        { type: "Exam", id: examId },
        "Exam",
        // { type: "ExamQuestion", id: examId },
        { type: "PartQuestion", id: partData._id },
      ],
    }),

    // Part Instruction Management
    // Part Management (Standalone)
    getParts: builder.query({
      query: (params = {}) => ({
        url: "/admin/part-instructions",
        params,
      }),
      providesTags: ["Part"],
    }),

    // Part Instruction Management
    updatePartInstruction: builder.mutation({
      query: ({ id, instructionData }) => ({
        url: `/admin/part-instructions/${id}`,
        method: "PATCH",
        body: instructionData, // Expects FormData
      }),
      invalidatesTags: ["PartInstruction"],
    }),

    // Class Management
    getClasses: builder.query({
      query: (params = {}) => ({
        url: "/admin/classes",
        params,
      }),
      providesTags: ["Classes"],
    }),

    createClass: builder.mutation({
      query: (classData) => ({
        url: "/admin/classes",
        method: "POST",
        body: classData,
      }),
      invalidatesTags: ["Classes"],
    }),

    updateClass: builder.mutation({
      query: ({ classId, classData }) => ({
        url: `/admin/classes/${classId}`,
        method: "PATCH",
        body: classData,
      }),
      invalidatesTags: ["Classes"],
    }),

    deleteClass: builder.mutation({
      query: (classId) => ({
        url: `/admin/classes/${classId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Classes"],
    }),

    // Exam Assignment Management (Class-based)
    getExamClassAssignments: builder.query({
      query: (params = {}) => ({
        url: `/admin/classes/exams/${params.examId}/class-assignments`,
        params,
      }),
      providesTags: ["ExamAssignment"],
    }),

    assignExamToClasses: builder.mutation({
      query: ({ examId, classIds }) => ({
        url: `/admin/classes/exams/${examId}/bulk-class-assign`,
        method: "POST",
        body: { classIds },
      }),
      invalidatesTags: ["ExamAssignment", "ClassExams"],
    }),

    removeExamClassAssignment: builder.mutation({
      query: ({ examId, classId }) => ({
        url: `/admin/classes/exams/${examId}/class-assignments/${classId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ExamAssignment", "ClassExams"],
    }),

    getExamAssignedClasses: builder.query({
      query: (examId) => `/admin/classes/exams/${examId}/class-assignments`,
      providesTags: (result, error, examId) => [
        { type: "ExamAssignment", id: examId },
      ],
    }),

    getClassAssignedExams: builder.query({
      query: (classId) => `/admin/classes/${classId}/assignments`,
      providesTags: (result, error, classId) => [
        { type: "ExamAssignment", id: classId },
      ],
    }),

    // Legacy user-based assignment (kept for backward compatibility)
    assignExamToUsers: builder.mutation({
      query: ({ examId, userIds }) => ({
        url: `/admin/classes/exams/${examId}/assign`,
        method: "POST",
        body: { userIds },
      }),
      invalidatesTags: ["ExamAssignment", "UserExams"],
    }),

    removeExamAssignment: builder.mutation({
      query: ({ examId, userId }) => ({
        url: `/admin/classes/exams/${examId}/users/${userId}/unassign`,
        method: "DELETE",
      }),
      invalidatesTags: ["ExamAssignment", "UserExams"],
    }),

    getExamAssignedUsers: builder.query({
      query: (examId) => `/admin/classes/exams/${examId}/assigned-users`,
      providesTags: (result, error, examId) => [
        { type: "ExamAssignment", id: examId },
      ],
    }),

    getUserAssignedExams: builder.query({
      query: (userId) => `/admin/classes/users/${userId}/assigned-exams`,
      providesTags: (result, error, userId) => [
        { type: "ExamAssignment", id: userId },
      ],
    }),

    // Reports
    getStudentPerformanceReport: builder.query({
      query: (params = {}) => ({
        url: "/admin/reports/student-performance",
        params,
      }),
      providesTags: ["Analytics"],
    }),

    // Admin Exercise Report (Class → Student → Day → Section)
    getAdminExerciseReport: builder.query({
      query: (classId) => `/admin/reports/exercises/${classId}`,
      providesTags: (result, error, classId) => [
        { type: "AdminExerciseReport", id: classId },
      ],
    }),

    // Admin Mock Test Report (Class → Student → Exam)
    getAdminMockTestReport: builder.query({
      query: (classId) => `/admin/reports/mock-tests/${classId}`,
      providesTags: (result, error, classId) => [
        { type: "AdminMockTestReport", id: classId },
      ],
    }),

    // Admin Public Mock Test analytics (overview | by-exam | by-user)
    getPublicMockTestStats: builder.query({
      query: ({ type = "overview", examId } = {}) => ({
        url: "/admin/public-mock-tests/stats",
        params: { type, ...(examId ? { examId } : {}) },
      }),
      transformResponse: (response) => response?.data ?? null,
      providesTags: ["PublicMockTestStats"],
    }),

    // Books / Collections (e.g. "ETS 2019").
    // Minimal model: { _id, title, description, year, examCount }.
    // Only list + create are needed for the inline exam-form UX.
    getBooks: builder.query({
      query: (params = {}) => ({
        url: "/admin/books",
        params,
      }),
      transformResponse: (response) => ({
        items: response?.data ?? [],
        pagination: response?.pagination ?? null,
      }),
      providesTags: ["Book"],
    }),

    createBook: builder.mutation({
      query: (body) => ({
        url: "/admin/books",
        method: "POST",
        body,
      }),
      transformResponse: (response) => response?.data ?? null,
      invalidatesTags: ["Book"],
    }),
  }),
});

export const {
  useGetDashboardStatsQuery,
  useGetAnalyticsQuery,
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useCreateUserMutation,
  useAddUserArrayMutation,
  useUpdateUserRoleMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetAssignmentsQuery,
  useGetAssignmentSubmissionsQuery,
  useDeleteAssignmentMutation,
  useGetAdminExamsQuery,
  useLazyGetAdminExamsQuery,
  useGetExamByIdQuery,
  useLazyGetExamByIdQuery,
  useGetAdminExamQuestionsQuery,
  useUpdateQuestionInExamMutation,
  useDeleteQuestionFromExamMutation,
  useCreateExamMutation,
  useUpdateAdminExamMutation,
  useGetExamAttemptsQuery,
  useDeleteAdminExamMutation,
  useParsePdfMutation,
  useBulkAddExamQuestionsMutation,
  useAddQuestionToExamMutation,
  useUpdateExamPartMutation,
  useUpdatePartInstructionMutation,
  // Class Management
  useGetClassesQuery,
  useLazyGetClassesQuery,
  useCreateClassMutation,
  useUpdateClassMutation,
  useDeleteClassMutation,
  // Class-based Exam Assignment
  useGetExamClassAssignmentsQuery,
  useAssignExamToClassesMutation,
  useRemoveExamClassAssignmentMutation,
  useGetExamAssignedClassesQuery,
  useGetClassAssignedExamsQuery,
  // Legacy user-based assignment
  useAssignExamToUsersMutation,
  useRemoveExamAssignmentMutation,
  useGetExamAssignedUsersQuery,
  useGetUserAssignedExamsQuery,
  useGetAllExamAttemptsQuery,
  useDeleteExamAttemptMutation,
  useResetExamAttemptMutation,
  useGetStudentPerformanceReportQuery,
  useGetPartsQuery,
  useGetQuestionsByPartIdQuery,
  // Admin Reports
  useGetAdminExerciseReportQuery,
  useLazyGetAdminExerciseReportQuery,
  useGetAdminMockTestReportQuery,
  useLazyGetAdminMockTestReportQuery,
  useGetPublicMockTestStatsQuery,
  useLazyGetPublicMockTestStatsQuery,
  // Books / Collections
  useGetBooksQuery,
  useCreateBookMutation,
} = adminApi;
